import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, XCircle, Undo2, Gift } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const planName = (code) => (code || 'your plan').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
const REASONS = [
  ['too_expensive', 'Too expensive'], ['not_using', 'Not using it enough'], ['missing_feature', 'Missing a feature'],
  ['switching', 'Switching to another tool'], ['just_testing', 'Just testing'], ['other', 'Other'],
];
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const syncLocalUser = (patch) => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    if (u) localStorage.setItem('user', JSON.stringify({ ...u, ...patch }));
    window.dispatchEvent(new Event('plan-updated'));
  } catch { /* ignore */ }
};

export const PlanCancelControls = ({ status, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  if (!status) return null;

  if (status.billing_exempt) {
    return (
      <div className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1" data-testid="billing-exempt-badge">
        <ShieldCheck size={14} /> Enterprise · Platform owner · Billing exempt
      </div>
    );
  }

  const run = async (path, success, body = {}) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}${path}`, body, { headers: headers() });
      toast.success(r.data?.message || success);
      setOpen(false);
      onChanged?.();
      return r.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const c = status.cancellation;
  if (c) {
    return (
      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="billing-cancellation-notice">
        <span className="text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-3 py-1" data-testid="billing-cancelled-badge">
          {c.type === 'trial' ? 'Trial cancelled' : 'Cancellation scheduled'} · access until {fmt(c.effective_at)}, then Free
        </span>
        {c.type === 'period_end' && (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => run('/billing/cancel/undo', 'Cancellation undone')} data-testid="billing-undo-cancel-btn">
            <Undo2 size={13} className="mr-1" /> Undo cancellation
          </Button>
        )}
      </div>
    );
  }

  const resumeNote = status.winback_resume_at && (
    <div className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-violet-800 bg-violet-50 border border-violet-200 rounded-full px-3 py-1" data-testid="billing-winback-active">
      <Gift size={13} /> Free month active · billing resumes {fmt(status.winback_resume_at)}
    </div>
  );
  const isTrial = status.plan_status === 'trialing';
  const isPaidActive = status.plan_status === 'active' && status.plan_code && status.plan_code !== 'free';
  if (!isTrial && !isPaidActive) return null;
  const endsOn = isTrial ? fmt(status.plan_trial_ends_at) : 'the end of your current billing period';

  const offer = status.winback_available && (isTrial
    ? { label: 'Extend my trial by 7 days — free', sub: 'Keep exploring with full access. No charge.' }
    : { label: 'Get 1 month free', sub: 'We skip your next charge. Cancel any time after.' });
  return (
    <>
      {resumeNote}
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}
        className="mt-3 text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800"
        data-testid={isTrial ? 'billing-cancel-trial-btn' : 'billing-cancel-plan-btn'}>
        <XCircle size={14} className="mr-1" /> {isTrial ? 'Cancel Trial' : 'Cancel Plan'}
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="billing-cancel-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>{isTrial ? 'Cancel your trial?' : 'Cancel your plan?'}</AlertDialogTitle>
            <AlertDialogDescription data-testid="billing-cancel-modal-text">
              Are you sure you want to cancel? You will lose access to {planName(status.plan_code)} features at the end of your {isTrial ? 'trial' : 'billing period'} on {endsOn}.
              {isTrial ? ' No charge will be made.' : ' You will not be billed again.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div className="text-xs font-semibold text-stone-600">Mind telling us why? (one tap)</div>
            <div className="flex flex-wrap gap-2" data-testid="billing-cancel-reasons">
              {REASONS.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setReason(k)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${reason === k ? 'bg-stone-900 text-white border-stone-900' : 'bg-white/70 text-stone-700 border-stone-200 hover:border-stone-400'}`}
                  data-testid={`billing-cancel-reason-${k}`}>{l}</button>
              ))}
            </div>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={2}
              placeholder="Anything we could do better? (optional)" data-testid="billing-cancel-note" />
            {offer && (
              <div className="rounded-xl border-2 border-violet-200 bg-violet-50/70 p-3 flex items-center gap-3" data-testid="billing-winback-offer">
                <Gift size={20} className="text-violet-700 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-violet-900">{offer.label}</div>
                  <div className="text-xs text-violet-700">{offer.sub}</div>
                </div>
                <Button size="sm" disabled={busy} onClick={() => run('/billing/winback', 'Offer applied').then((d) => d?.plan_trial_ends_at && syncLocalUser({ plan_trial_ends_at: d.plan_trial_ends_at }))}
                  className="bg-violet-700 hover:bg-violet-800 text-white" data-testid="billing-winback-accept-btn">Yes, I'll stay</Button>
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="billing-cancel-keep-btn">Keep {isTrial ? 'trial' : 'plan'}</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(e) => { e.preventDefault(); run('/billing/cancel', 'Cancelled', { reason: reason || null, note: note || null }).then((d) => d && syncLocalUser({ plan_cancelled: true })); }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="billing-cancel-confirm-btn"
            >
              {busy ? 'Cancelling…' : isTrial ? 'Yes, cancel trial' : 'Yes, cancel plan'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
